import {
  Box,
  Grid,
  InputLabel,
  type InputLabelProps,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import { palette } from "~frontend/theme";

function Label({
  optional,
  children,
  ...rest
}: { optional?: boolean } & InputLabelProps) {
  return (
    <InputLabel shrink {...rest}>
      <FlexBox alignItems="center" gap={1}>
        {children}
        {optional && <Typography>(optional)</Typography>}
      </FlexBox>
    </InputLabel>
  );
}

export function RhfDateTimePicker<T extends FieldValues>({
  name,
  dateLabel,
  timeLabel,
  optional = false,
  variant = "outlined",
  disabled = false,
  minDate,
  minTime,
  inputLabelSlotProps,
  control,
}: {
  name: Path<T>;
  dateLabel: string;
  timeLabel: string;
  optional?: boolean;
  variant?: "outlined" | "filled" | "standard";
  disabled?: boolean;
  minDate?: DateTime;
  minTime?: DateTime;
  inputLabelSlotProps?: InputLabelProps;
  control: Control<T>;
}): ReactElement {
  const { field, fieldState } = useController({
    control,
    name,
  });

  const value = (() => {
    if (!field.value) {
      return null;
    }
    if (typeof field.value === "string") {
      return DateTime.fromISO(field.value);
    }
    // When a DateTimePicker is rendered using data from the api,
    // the timestamp from the api comes back in epoch time as opposed to a string;
    if (typeof field.value === "number") {
      return DateTime.fromMillis(field.value);
    }
    const raw = field.value as unknown;
    if (raw instanceof Date) {
      return DateTime.fromJSDate(raw);
    }
    return null;
  })();

  function handleDateChange(newDate: DateTime | null) {
    if (!newDate) {
      field.onChange(null);
      return;
    }

    // Don't update if the date is invalid
    if (!newDate.isValid) {
      return;
    }

    if (value && value.isValid) {
      // Preserve time from existing value
      const combined = newDate.set({
        hour: value.hour,
        minute: value.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    } else {
      // No existing value, set to start of day
      field.onChange(newDate.startOf("day").toUTC().toISO());
    }
  }

  function handleTimeChange(newTime: DateTime | null) {
    if (!newTime) {
      if (optional) {
        field.onChange(null);
      }
      return;
    }

    // Don't update if the time is invalid
    if (!newTime.isValid) {
      return;
    }

    if (value && value.isValid) {
      // Preserve date from existing value, update time
      const combined = value.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    } else {
      // No existing value, use today's date with selected time
      const combined = DateTime.now().set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: 0,
        millisecond: 0,
      });
      field.onChange(combined.toUTC().toISO());
    }
  }

  const isOutlined = variant === "outlined";

  const pickerSx = {
    width: "100%",
    mb: 0,
    ...(isOutlined
      ? {
          "& .MuiPickersInputBase-root": {
            borderRadius: 0,
            backgroundColor: palette.white,
          },
        }
      : {}),
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 6 }}>
        <Box>
          {dateLabel ? (
            <Label optional={optional} {...inputLabelSlotProps}>
              {dateLabel}
            </Label>
          ) : null}
          <DatePicker
            value={value}
            onChange={handleDateChange}
            disabled={disabled}
            minDate={minDate}
            slotProps={{
              textField: {
                variant,
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
                sx: { ...pickerSx, pt: dateLabel ? 0 : undefined },
              },
            }}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Box>
          {timeLabel ? (
            <Label optional={optional} {...inputLabelSlotProps}>
              {timeLabel}
            </Label>
          ) : null}
          <TimePicker
            value={value}
            onChange={handleTimeChange}
            disabled={disabled}
            minTime={minTime}
            slotProps={{
              textField: {
                variant,
                error: !!fieldState.error,
                sx: { ...pickerSx, pt: timeLabel ? 0 : undefined },
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
